import { ActionOrTriggerListTypeEnum, IActionOrTriggerListPageParams } from "@app/static"
import { getProxyDispatcher, useProxyWatch, useTranslation, Log } from "@fanfanlo"
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, FormControlLabel, Grid2, Stack, Switch, TextField } from "@mui/material"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { childToProjectDataUtils, IChildToProjectData } from "@droid/project-v2-interface"
import { IA11yNodeInfo, a11yNodeProperties, a11yNodeRealizedKeys, IA11yNode, NodeAction } from "../types"
import { NodeActionUI } from "../components"
import { actionA11yNodeExecuteUtils, IActionA11yNodeExecute } from "./node-execute"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const fileLog = new Log(false, 'NodeExecuteUI_f')
fileLog.pause = true

function SelectConditions({ data }: { data: IActionA11yNodeExecute }) {
    const node = data.javaData.info?.node
    if (!node) {
        return <></>
    }

    const { t } = useTranslation("droid-project-v2-ext/actions/a11y/node-execute/content")
    const [showAll, setShowAll] = useState(false)
    const [keys, setKeys] = useState(showAll ? Object.keys(a11yNodeProperties) : a11yNodeRealizedKeys)

    useEffect(() => {
        setKeys(showAll ? Object.keys(a11yNodeProperties) : a11yNodeRealizedKeys)
    }, [showAll])

    return (
        <>
            <Box sx={{ width: "100%" }}>
                <Box>
                    <FormControlLabel
                        control={<Switch data-testid="NodeExecuteUI__showAllSwitch__01JFXYZZ1P5Q6R7S8T9U0V1W2" checked={Boolean(showAll)} onChange={(e) => setShowAll(Boolean(e.target.checked))} />}
                        label={t("content.showNodeAllProperties")}
                    />
                </Box>
                {keys.map((key, index) => {
                    return (
                        <Box key={key} sx={{ width: "100%", wordBreak: "break-all" }}>
                            <FormControlLabel
                                control={<Checkbox data-testid={`NodeExecuteUI__propertyCheckbox__01JFXYZA2Q6R7S8T9U0V1W2X3__${index}`} checked={data.javaData.selectedProperties.includes(key as keyof IA11yNode)} onChange={(e) => {
                                    if (e.target.checked) {
                                        data.javaData.selectedProperties.push(key as keyof IA11yNode)
                                    } else {
                                        data.javaData.selectedProperties = data.javaData.selectedProperties.filter(k => k !== key)
                                    }
                                }} />}
                                label={`${key}::${JSON.stringify(node[key as keyof IA11yNode])}`}
                            />
                        </Box>
                    )
                })}
            </Box>
        </>
    )
}

function WaitUI({ data }: { data: IActionA11yNodeExecute }) {
    if (!data.javaData.wait) {
        data.javaData.wait = {
            time: 1000,
            interval: 20,
            invisible: false
        }
    }
    const { t } = useTranslation("droid-project-v2-ext/actions/a11y/node-execute/content")
    const [time] = useProxyWatch<IActionA11yNodeExecute, number>(data, 'javaData.wait.time', data.javaData.wait.time || 1000)
    const [interval] = useProxyWatch<IActionA11yNodeExecute, number>(data, 'javaData.wait.interval', data.javaData.wait.interval)
    const [invisible] = useProxyWatch<IActionA11yNodeExecute, boolean>(data, "javaData.wait.invisible", data.javaData.wait.invisible)

    return (
        <>
            <Stack direction="column" sx={{ width: "100%" }} spacing={2}>
                <TextField data-testid="NodeExecuteUI__waitTimeInput__01JFXYZB3R7S8T9U0V1W2X3Y4" type="number" value={parseInt(time.toString())} onChange={(e) => data.javaData.wait.time = parseInt(e.target.value)} label={t("content.wait.time")} />
                <TextField data-testid="NodeExecuteUI__waitIntervalInput__01JFXYZC4S8T9U0V1W2X3Y4Z5" type="number" value={parseInt(interval.toString())} onChange={(e) => data.javaData.wait.interval = parseInt(e.target.value)} label={t("content.wait.interval")} />
                <FormControlLabel control={<Checkbox data-testid="NodeExecuteUI__waitInvisibleCheckbox__01JFXYZD5T9U0V1W2X3Y4Z5A6" checked={invisible} onChange={(e) => data.javaData.wait.invisible = e.target.checked} />} label={t("content.wait.invisible")} />
            </Stack>
        </>
    )
}

export function UI(data: IActionA11yNodeExecute) {
    const onInfoSelectedEventType = 'onInfoSelected'
    const { t } = useTranslation("droid-project-v2-ext/actions/a11y/node-execute/content")
    const router = useRouter()
    const backPath = router.pathname
    const [nodeAction, _setNodeAction] = useState(data.javaData.action)
    const [open, setOpen] = useState(false)

    function setNodeAction(nodeAction: NodeAction) {
        _setNodeAction(nodeAction)
        data.javaData.action = nodeAction
    }

    const [text, _setText] = useState(data.javaData.setText)
    function setText(text: string) {
        _setText(text)
        data.javaData.setText = text
    }

    function onChangeClick() {
        const projectInfo: IChildToProjectData = {}
        childToProjectDataUtils.actionList.getActionListByProp(data, projectInfo)
        // 修改：属性从 .id 改为 .ulid，URL参数名从 xxxId 改为 xxxUlid
        if (!projectInfo.project?.ulid || !projectInfo.triggerActionGroup?.ulid || !projectInfo.triggerAction?.ulid || !projectInfo.actionGroup?.ulid) {
            console.error('UpdateAppLink projectInfo is undefined', projectInfo)
            return
        }

        const query: IActionOrTriggerListPageParams = {
            backPath,
            projectUlid: projectInfo.project?.ulid,
            triggerActionGroupUlid: projectInfo.triggerActionGroup?.ulid,
            triggerActionUlid: projectInfo.triggerAction?.ulid,
            actionGroupUlid: projectInfo.actionGroup?.ulid,
            type: ActionOrTriggerListTypeEnum.updateAction,
            actionUlid: data.ulid,
            actionType: actionA11yNodeExecuteUtils.type,
        }
        actionA11yNodeExecuteUtils.onClickChange?.({ query, onDataSelectedEventType: onInfoSelectedEventType })
    }

    useEffect(() => {
        return getProxyDispatcher(data).addListener(onInfoSelectedEventType, (info: IA11yNodeInfo) => {
            data.javaData.info = info
        })
    })

    if (!nodeAction) {
        return (
            <>
                <Box>
                    no node action!!!
                </Box>
            </>
        )
    }

    return (
        <>
            <Box sx={{ width: "100%" }}>
                <Accordion expanded={Boolean(open)} onChange={(e, expanded) => setOpen(Boolean(expanded))}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <span>{t("content.title")}</span>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid2 container sx={{ width: "100%" }}>
                            <Grid2 size="grow" container>
                                <Stack direction="column" spacing={2}>
                                    <NodeActionUI nodeAction={nodeAction} setNodeAction={setNodeAction} text={text || ""} setText={setText} />
                                    <WaitUI data={data} />
                                    <SelectConditions data={data} />
                                </Stack>
                            </Grid2>
                            <Grid2 size={"auto"}>
                                <Button data-testid="NodeExecuteUI__changeButton__01JFXYZE6U0V1W2X3Y4Z5A6B7" onClick={onChangeClick}>
                                    <span>{t("content.changeBtnLabel")}</span>
                                </Button>
                            </Grid2>
                        </Grid2>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </>
    )
}
